import { getReportToken } from '../../../services/reportsApi';
// The parser every other backend's responses already go through. It unwraps
// `{ encryptedText }`, decrypts it, and FALLS BACK TO PLAIN JSON when there is
// nothing to decrypt — which is what lets this ship in either order relative to
// the backend's RESPONSE_ENCRYPTION_ENABLED flag.
import { parseResponse } from '../../../services/crypto';
import { messageFromRevampErrorBody } from '../../../utils/revampApiErrorMessage';
import { reportApiTracker } from '../../../utils/reportApiTracker';
import type {
  AcquisitionSourceRequest,
  AcquisitionSourceResponse,
  AvgSpendBySegmentRequest,
  CustomerDetailsRequest,
  CustomerDetailsResponse,
  RefreshSegmentBreakdownResponse,
  DrilldownCustomersRequest,
  DrilldownStatsResponse,
  DrilldownTarget,
  AvgSpendBySegmentResponse,
  ChannelPerformanceRequest,
  CohortRetentionRequest,
  CohortRetentionResponse,
  ChannelPerformanceResponse,
  CustomerDirectoryRequest,
  CustomerDirectoryResponse,
  CustomerSegmentBreakdownResponse,
  CustomerStampKpisRequest,
  CustomerStampKpisResponse,
  ReportPeriod,
} from '../../../interface/customersReportInterface';

/*
 * Customers Report endpoints live on the NestJS reports backend — the same
 * service as the Voice Agent report, which is why they share its base-URL env
 * var.
 *
 * Responses go through `parseResponse`, the same parser the legacy backend's
 * responses use. That backend can now return `{ encryptedText }` when
 * RESPONSE_ENCRYPTION_ENABLED is set, and `parseResponse` falls back to plain
 * JSON when it is not — so this file works against an encrypting backend and a
 * plain one, and the two can be deployed in either order.
 *
 * It still does not use `fetchReport`: that carries the legacy backend's own
 * headers and error envelope, which this backend does not speak.
 */
const NEST_REPORTS_BASE = (process.env.REACT_APP_API_ENDPOINT_VOICE_AGENT || '').replace(/\/$/, '');

export const CUSTOMER_REPORTS_API_BASE = `${NEST_REPORTS_BASE}/api/clickhouse/customer-reports`;

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getReportToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  reportApiTracker.trackStart();
  let res: Response;
  try {
    res = await fetch(url, { headers, signal });
  } catch (error) {
    reportApiTracker.trackFailure();
    throw error;
  }

  let data: any = null;
  try {
    // Read as text, not res.json(): an encrypted body is a JSON string wrapping
    // base64, and parseResponse has to see the raw text to tell the two apart.
    data = parseResponse(await res.text());
  } catch {
    /* non-JSON, non-decryptable body */
  }
  if (!res.ok) {
    reportApiTracker.trackFailure();
    // Errors are never encrypted — the backend interceptor only touches
    // successful bodies — so this still reads the real message.
    throw new Error(messageFromRevampErrorBody(data, `Customers Report request failed (${res.status})`));
  }
  reportApiTracker.trackSuccess();
  return data as T;
}

/**
 * Adds the period to a query: both dates, or neither. Neither is Today — the
 * screen leaves the dates off when the selected range is today, and the backend
 * then reads the live tables (`v1_live_fact_orders`) instead of the historical
 * ones. A lone or blank date is a 400, so the pair travels together or not at
 * all; `URLSearchParams` would otherwise send a missing one as "undefined".
 */
const setPeriod = (params: URLSearchParams, { startDate, endDate }: ReportPeriod): URLSearchParams => {
  if (startDate && endDate) {
    params.set('startDate', startDate);
    params.set('endDate', endDate);
  }
  return params;
};

export const getCustomerStampKpisApi = (
  { locationId, startDate, endDate, compStartDate, compEndDate }: CustomerStampKpisRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({ location_id: locationId }), { startDate, endDate });
  // Same params as Snapshot's comparison call (ZAR-1661): Today carries
  // `compStartDate` alone, every other period both - and neither when the
  // selection's shape has no comparison.
  if (compStartDate) params.set('compStartDate', compStartDate);
  if (compEndDate) params.set('compEndDate', compEndDate);
  return getJson<CustomerStampKpisResponse>(`${CUSTOMER_REPORTS_API_BASE}/stamp-kpis?${params}`, signal);
};

/* Deliberately no dates: segments are a snapshot of the customer base, and the
   date filter does not affect RFM scoring (CUST-10.1). */
export const getCustomerSegmentBreakdownApi = (
  { locationId }: { locationId: string },
  signal?: AbortSignal,
) => {
  const params = new URLSearchParams({ location_id: locationId });
  return getJson<CustomerSegmentBreakdownResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/segment-breakdown-kpi?${params}`,
    signal,
  );
};

/**
 * Customer Directory (CUST-8.1) — one page, with sort, search and paging all
 * resolved server-side over the whole directory.
 *
 * The export (CUST-9.2) calls this same function with `page: 1` and
 * `pageSize: totalCustomers`, which returns the entire filtered, sorted set in
 * a single response instead of walking the pages.
 */
export const getCustomerDirectoryApi = (
  {
    locationId, startDate, endDate, page, pageSize, sortBy, sortOrder, search,
    drill, segmentKey, sourceKey, churnThresholdDays,
  }: CustomerDirectoryRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({
    location_id: locationId,
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
  }), { startDate, endDate });
  // With `drill` this same endpoint returns a stamp-KPI drill table instead of
  // the whole directory - one query, so the table can never select a different
  // set of customers from the header that opened it. Without it, nothing
  // changes. `segmentKey` only travels for a segment drill; sending it
  // otherwise would imply it filters something.
  if (drill) {
    params.set('drill', drill);
    if (drill === 'segment' && segmentKey) params.set('segmentKey', segmentKey);
    if (drill === 'acquisition' && sourceKey) params.set('sourceKey', sourceKey);
    if (churnThresholdDays != null) {
      params.set('churn_threshold_days', String(churnThresholdDays));
    }
  }
  // A blank `search` would still be sent as `search=`, which the backend reads
  // as a real (empty) term rather than "no search" — so omit it entirely.
  const term = search?.trim();
  if (term) params.set('search', term);

  return getJson<CustomerDirectoryResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/customer-directory-kpi?${params}`,
    signal,
  );
};

/**
 * Channel Performance (CUST-13.1). Takes the period, unlike the segment
 * breakdown — channel mix is a period slice, not a snapshot.
 */
export const getChannelPerformanceApi = (
  { locationId, startDate, endDate }: ChannelPerformanceRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({ location_id: locationId }), { startDate, endDate });
  return getJson<ChannelPerformanceResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/channel-performance-kpi?${params}`,
    signal,
  );
};

/**
 * Cohort Retention (CUST-11.1).
 *
 * Only `endDate` means anything — the window is always the 12 months ending
 * there — but every Customers Report endpoint takes **both dates or neither**,
 * so `startDate` is sent with the same value rather than omitted. Omitting it
 * alone is a 400 ("startDate should not be empty"); the backend validates the
 * pair and then ignores `startDate`. Omitting both is Today.
 */
export const getCohortRetentionApi = (
  { locationId, startDate, endDate }: CohortRetentionRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({ location_id: locationId }), { startDate, endDate });
  return getJson<CohortRetentionResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/cohort-retention-kpi?${params}`,
    signal,
  );
};

/** Avg Spend by RFM Segment (CUST-12.1) — period-scoped, with a delta vs the comparison window. */
export const getAvgSpendBySegmentApi = (
  { locationId, startDate, endDate, compStartDate, compEndDate }: AvgSpendBySegmentRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({ location_id: locationId }), { startDate, endDate });
  // QA #27 - the same comp params as stamp-kpis (Today: `compStartDate` alone).
  if (compStartDate) params.set('compStartDate', compStartDate);
  if (compEndDate) params.set('compEndDate', compEndDate);
  return getJson<AvgSpendBySegmentResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/avg-spend-by-segment-kpi?${params}`,
    signal,
  );
};

/**
 * Acquisition Source (CUST-19.1). Period-scoped like Channel Performance, but
 * note `avgClv` on each row is historical rather than period - see the type.
 */
export const getAcquisitionSourceApi = (
  { locationId, startDate, endDate }: AcquisitionSourceRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({ location_id: locationId }), { startDate, endDate });
  return getJson<AcquisitionSourceResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/acquisition-source-kpi?${params}`,
    signal,
  );
};

/** The stamp drill HEADER still has its own endpoint - it returns figures, not rows. */
const drilldownParams = (t: DrilldownTarget): URLSearchParams => {
  const params = setPeriod(new URLSearchParams({
    location_id: t.locationId,
    drill: t.drill,
  }), t);
  if (t.drill === 'segment' && t.segmentKey) params.set('segmentKey', t.segmentKey);
  return params;
};

/** CUST-6.1 / 6.2 / 6.3 / CUST-2.2 - the drill-down header. */
export const getDrilldownStatsApi = (target: DrilldownTarget, signal?: AbortSignal) =>
  getJson<DrilldownStatsResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/stamp-kpi-drilldowns?${drilldownParams(target)}`,
    signal,
  );

/**
 * CUST-5.1 - one page of a drill's customer table.
 *
 * **This is `customer-directory-kpi`, not a separate endpoint.** The drill
 * table and the Customer Directory are the same table with the same row shape,
 * so they are one query; `drill` is what narrows it. Kept as its own exported
 * function purely so the drill's saga reads clearly - it adds nothing but the
 * parameter.
 */
export const getDrilldownCustomersApi = (
  request: DrilldownCustomersRequest,
  signal?: AbortSignal,
) => getCustomerDirectoryApi(request, signal);

/**
 * CUST-7 / 16 / 17 - one customer: profile, RFM detail and order log.
 *
 * The ROW-CLICK call, and it is the same call from the directory table and from
 * any drill table, because both now return the same row carrying `customerId`.
 *
 * Both dates, or neither for Today (like every Customers Report endpoint), but
 * only `endDate` does anything here: the order log is a full history, not a
 * period slice, and everything is computed as of that date so the segment shown
 * matches the badge on the row that was clicked.
 */
export const getCustomerDetailsApi = (
  { locationId, customerId, startDate, endDate, page, pageSize, sortBy, sortOrder, search }: CustomerDetailsRequest,
  signal?: AbortSignal,
) => {
  const params = setPeriod(new URLSearchParams({
    location_id: locationId,
    customerId,
  }), { startDate, endDate });
  if (page != null) params.set('page', String(page));
  if (pageSize != null) params.set('pageSize', String(pageSize));
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  // QA #57 - only when there is a term; a blank one is "no search".
  if (search && search.trim()) params.set('search', search.trim());

  return getJson<CustomerDetailsResponse>(
    `${CUSTOMER_REPORTS_API_BASE}/customer-details-drilldowns?${params}`,
    signal,
  );
};

/**
 * CUST-3.4 — Refresh Segments. POST, because it rebuilds the RFM scores rather
 * than reading them.
 *
 * Called directly rather than through a saga: it is a one-shot action whose
 * result is a toast and a refetch, so there is no store state for it to own —
 * the same reasoning as the directory export.
 */
export const refreshSegmentBreakdownApi = async (
  signal?: AbortSignal,
): Promise<RefreshSegmentBreakdownResponse> => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getReportToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  reportApiTracker.trackStart();
  let res: Response;
  try {
    res = await fetch(`${CUSTOMER_REPORTS_API_BASE}/refresh-segment-breakdown`, {
      method: 'POST',
      headers,
      signal,
    });
  } catch (error) {
    reportApiTracker.trackFailure();
    throw error;
  }

  let data: any = null;
  try {
    data = parseResponse(await res.text());
  } catch {
    /* non-JSON, non-decryptable body */
  }
  if (!res.ok) {
    reportApiTracker.trackFailure();
    throw new Error(messageFromRevampErrorBody(data, `Segment refresh failed (${res.status})`));
  }
  reportApiTracker.trackSuccess();
  return data as RefreshSegmentBreakdownResponse;
};
